export interface GrammarError {
    message: string;
    context: string;
    suggestions: string[];
}

export interface ValidationResult {
    isCorrect: boolean;
    feedback: string;
    errors?: GrammarError[];
}

export async function analyzeSentence(targetWord: string, sentence: string): Promise<ValidationResult> {
    // 1. Basic check: Does it contain the word?
    if (!sentence.toLowerCase().includes(targetWord.toLowerCase())) {
        return {
            isCorrect: false,
            feedback: `You need to include the word "${targetWord}" in your sentence.`
        };
    }

    // 2. Length check
    if (sentence.length <= targetWord.length + 5) {
        return {
            isCorrect: false,
            feedback: `Your sentence is too short. Please write a complete sentence using "${targetWord}".`
        };
    }

    // 3. LanguageTool API Grammar Check
    try {
        const response = await fetch('https://api.languagetool.org/v2/check', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                text: sentence,
                language: 'en-US',
            })
        });

        if (!response.ok) {
            throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();

        // If LanguageTool found errors, format and return them
        if (data.matches && data.matches.length > 0) {
            const errors: GrammarError[] = data.matches.map((match: {
                message: string;
                offset: number;
                length: number;
                replacements?: Array<{ value: string }>;
            }) => {
                // Get a snippet of the context around the error
                const start = Math.max(0, match.offset - 15);
                const end = Math.min(sentence.length, match.offset + match.length + 15);
                let contextSnippet = sentence.substring(start, end);
                if (start > 0) contextSnippet = '...' + contextSnippet;
                if (end < sentence.length) contextSnippet = contextSnippet + '...';

                return {
                    message: match.message,
                    context: contextSnippet,
                    suggestions: match.replacements?.map((r) => r.value).slice(0, 3) || []
                };
            });

            return {
                isCorrect: false,
                feedback: 'We found some grammar or spelling issues in your sentence:',
                errors
            };
        }

        // Sentence is grammatically correct and includes the target word
        return {
            isCorrect: true,
            feedback: 'Great sentence! It looks grammatically correct.'
        };
    } catch (error) {
        console.error('Error validating grammar:', error);
        // Fallback to basic validation if API fails so the user isn't blocked
        return {
            isCorrect: true,
            feedback: 'Good job using the word!'
        };
    }
}

export async function validateGrammar(sentence: string): Promise<ValidationResult> {
    if (sentence.trim().length < 3) {
        return {
            isCorrect: false,
            feedback: `Your sentence is too short.`
        };
    }

    try {
        const response = await fetch('https://api.languagetool.org/v2/check', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                text: sentence,
                language: 'en-US',
            })
        });

        if (!response.ok) {
            throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (data.matches && data.matches.length > 0) {
            const errors: GrammarError[] = data.matches.map((match: {
                message: string;
                offset: number;
                length: number;
                replacements?: Array<{ value: string }>;
            }) => {
                const start = Math.max(0, match.offset - 15);
                const end = Math.min(sentence.length, match.offset + match.length + 15);
                let contextSnippet = sentence.substring(start, end);
                if (start > 0) contextSnippet = '...' + contextSnippet;
                if (end < sentence.length) contextSnippet = contextSnippet + '...';

                return {
                    message: match.message,
                    context: contextSnippet,
                    suggestions: match.replacements?.map((r) => r.value).slice(0, 3) || []
                };
            });

            return {
                isCorrect: false,
                feedback: 'We found some grammar or spelling issues:',
                errors
            };
        }

        return {
            isCorrect: true,
            feedback: 'Great sentence! It looks grammatically correct.'
        };
    } catch (error) {
        console.error('Error validating grammar:', error);
        return {
            isCorrect: true,
            feedback: 'Looking good!'
        };
    }
}
